import Image, {ImageProps} from 'next/image';
import {useState} from 'react';
import {useMediaQuery} from '@/hooks';
import {OptimizedAssetFragment} from './Asset.atom';
import {css} from '@panda/css';

const rootStyle = css({
	position: 'relative',
	'& img': {
		position: 'relative !important'
	}
});

export interface PictureProps extends Omit<ImageProps, 'src'> {
	className?: string;
	src: string;
	fallbackImage?: OptimizedAssetFragment['fallbackImage'];
	mobileImage?: OptimizedAssetFragment['mobileImage'];
}

const MAX_RETRIES = 3;

export const Picture = ({
	id,
	src,
	alt,
	width,
	height,
	fallbackImage,
	mobileImage,
	...defaultProps
}: PictureProps) => {
	const [attempt, setAttempt] = useState(0);
	const [desktopImageUrl, setDesktopImageUrl] = useState(src);
	const [mobileImageUrl, setMobileImageUrl] = useState(mobileImage?.url || src);
	const isMobile = useMediaQuery('sm');

	const imageProps: ImageProps =
		isMobile && mobileImage
			? {
					...defaultProps,
					id: mobileImage.id || id,
					alt: mobileImage.altText || alt,
					width: mobileImage.width || width,
					height: mobileImage.height || height,
					src: mobileImageUrl,
					onError: () => {
						if (attempt < MAX_RETRIES) {
							setAttempt((prev) => prev + 1);
							setMobileImageUrl(mobileImage?.url || '');
						} else {
							console.error('Picture image failed to load after max retries');
						}
					}
				}
			: {
					...defaultProps,
					id,
					alt,
					width,
					height,
					src: desktopImageUrl,
					onError: () => {
						if (attempt < MAX_RETRIES) {
							setAttempt((prev) => prev + 1);
							setDesktopImageUrl(fallbackImage?.url || '');
						} else {
							console.error('Picture image failed to load after max retries');
						}
					}
				};

	return (
		<picture className={rootStyle}>
			<source media="(max-width: 767px)" srcSet={mobileImageUrl} />
			<source media="(min-width: 768px)" srcSet={desktopImageUrl} />
			<Image {...imageProps} />
		</picture>
	);
};
